import type { ISubmitOpenIDAuthCodeByProviderIDCriteria } from "@mne-authentication/api";
import queryString from "query-string";

import type { IntlShape } from "@mnemonic/intl";
import { defineMessages } from "@mnemonic/intl";
import { UrlUtils } from "@mnemonic/pure-utils";

import generateNonce from "../../../utils/generateNonce";
import { setOpenIdFlowSessionStorage } from "../../../utils/openIdFlowSessionStorage";
import { extractAuthResponse } from "../../../utils/parseUrl";
import { saveLoginDetails } from "./saveLoginDetails";

export interface IErrorData {
  error: boolean;
  message: string;
  responseCode: number;
}

export const createCriteriaForAuthCode = (provider: string): ISubmitOpenIDAuthCodeByProviderIDCriteria => {
  const authResponse = extractAuthResponse();
  return {
    authorizationCode: authResponse?.code,
    flow: authResponse?.code ? "authorizationCode" : "idToken",
    idToken: authResponse?.idToken,
    provider,
  };
};
export const messages = defineMessages({
  email: { defaultMessage: "Your email at your organization", id: "email" },

  helperText: {
    defaultMessage: "You will be sent an login uri so that you can login with your organization",
    id: "helperText",
  },
  missingEmail: { defaultMessage: "Email is required", id: "missingEmail" },
  invalidEmail: { defaultMessage: "Invalid email format", id: "invalidEmail" },
});

export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validateEmail = (email: string, intl: IntlShape) => {
  if (!email) return intl.formatMessage(messages.missingEmail);
  if (!isValidEmail(email)) return intl.formatMessage(messages.invalidEmail);
  return undefined;
};

export function createOpenIdAuthState(nextUrl: string) {
  const nonce = generateNonce();
  localStorage.setItem("openid.nonce", nonce);
  const state = UrlUtils.encodeUrlCriteria({ type: "authentication", nextURI: nextUrl, nonce });
  return { state, nonce };
}

/**
 * Resolves OpenID authentication context from the URL and local storage.
 *
 * - Provider from URL takes precedence over stored provider
 * - `authenticate=true` enables auto-authentication
 */
export function resolveOpenIdAuthContext(search: string) {
  const { provider, authenticate } = queryString.parse(search) as { provider?: string; authenticate?: string };
  const shortnameOrId = provider ?? localStorage.getItem("openid.provider");
  return { provider: shortnameOrId, authenticate: authenticate === "true" };
}

/**
 * Initiates an OpenID authentication redirect.
 *
 * Side effects:
 * - Persists pre-auth flow state for debugging/observability
 * - Triggers a full page redirect to the identity provider
 *
 * Calling this function unloads the current frontend instance.
 */
export function startOpenIdAuthentication(providerURI: string, authState: string): void {
  saveLoginDetails({ method: "openid", byPassResponseCode: true });
  setOpenIdFlowSessionStorage("preAuthRequestState", authState);
  setOpenIdFlowSessionStorage("preAuthResponseState", new URL(providerURI).searchParams.get("state"));
  window.location.replace(providerURI);
}

export const flushOpenIdNonce = () => {
  return localStorage.removeItem("openid.nonce");
};

export const getStoredOpenIdNonce = () => {
  return localStorage.getItem("openid.nonce");
};

/**
 * Persists the raw provider response state for debugging/traceability.
 */
export const persistProviderResponseState = (state?: string) => {
  if (state) {
    setOpenIdFlowSessionStorage("providerResponseState", state);
  }
};

interface IDecodedAuthState {
  type: string;
  nextURI: string;
  nonce?: string;
  id?: string;
}

/**
 * Decodes and parses the OpenID `state` parameter.
 *
 * Throws if decoding fails or state is malformed.
 */
export const decodeAuthState = (state: string): IDecodedAuthState => {
  return UrlUtils.decodeUrlCriteria<IDecodedAuthState>(decodeURIComponent(state));
};

/**
 * Handles an authentication state response.
 *
 * - Validates nonce against stored value
 * - Submits auth code on success
 * - Fails authentication on nonce mismatch
 *
 * Nonce is always flushed after handling.
 */
export const handleAuthenticationState = ({
  decodedState,
  submitAuthCode,
  setErrorData,
  errorData,
}: {
  decodedState: IDecodedAuthState;
  submitAuthCode: ({ nextUrl }: { nextUrl: string | null }) => void;
  setErrorData: React.Dispatch<React.SetStateAction<IErrorData>>;
  errorData: IErrorData;
}) => {
  const storedNonce = getStoredOpenIdNonce();
  if (storedNonce === decodedState.nonce) {
    submitAuthCode({ nextUrl: decodedState.nextURI });
  } else {
    setErrorData({ ...errorData, error: true, message: `Nonce does not match, ${storedNonce}, ${decodedState.nonce}` });
  }

  flushOpenIdNonce();
};

/**
 * Routes handling based on decoded OpenID state type.
 *
 * Supported types:
 * - authentication
 * - authorization
 *
 * Unknown types fall back to submitting auth without a nextURI.
 */
export const handleDecodedState = ({
  decodedState,
  submitAuthCode,
  authorize,
  setErrorData,
  errorData,
}: {
  decodedState: IDecodedAuthState;
  submitAuthCode: ({ nextUrl }: { nextUrl: string | null }) => void;
  authorize: ({ nextUrl, stateID }: { nextUrl: string; stateID: string }) => void;
  setErrorData: React.Dispatch<React.SetStateAction<IErrorData>>;
  errorData: IErrorData;
}) => {
  switch (decodedState.type) {
    case "authentication":
      handleAuthenticationState({ decodedState, submitAuthCode, setErrorData, errorData });
      break;

    case "authorization":
      authorize({ nextUrl: decodedState.nextURI, stateID: decodedState.id! });
      break;

    default:
      flushOpenIdNonce();
    // submitAuthCode({ nextUrl: null });
  }
};
